/**
 * Eine Zahlenzeile unter der Karte „Jetzt" — ersetzt die vier Kacheln und die
 * Auftragslage (Plan Projekt-Tracking, Etappe 2). Der Auftragswert steht nur noch
 * einmal. Seit 03.10.2026 steht hier auch der Stundenlohn der Inhaber (Gewinn je
 * Inhaberstunde) — die Gewinnermittlung als eigene Tabelle entfällt (Benjamin: sie
 * zeigte doppelt, was Kopf und Kostensektionen schon zeigen).
 */
import { formatEur } from "../../lib/planer";
import type { ProjektAuftragswert } from "../../lib/types";

type Props = {
  wert: ProjektAuftragswert | undefined; gewinn: number; netto: number;
  stundenlohn?: { betrag: number; inhaberStunden: number; helferStunden: number };
  /** Wie der Gewinn entsteht — steht als Hinweis am Gewinn (früher die Tabelle Gewinnermittlung). */
  gewinnWeg?: string;
};

export default function KopfZahlen({ wert, gewinn, netto, stundenlohn, gewinnWeg }: Props) {
  const marge = netto > 0 ? (gewinn / netto) * 100 : 0;
  const eur = (n: number) => formatEur(n, { round: true });
  const zahlen: { was: string; wert: string; ton?: string; titel?: string }[] = [
    { was: "Auftragswert", wert: eur(wert?.auftragswert ?? netto), titel: wert?.herkunft_text || undefined },
  ];
  if (wert && (wert.anzahl_rechnungen > 0 || wert.gestellt > 0)) {
    zahlen.push({ was: "Gestellt", wert: eur(wert.gestellt) }, { was: "Bezahlt", wert: eur(wert.bezahlt) });
    if (wert.offen > 0) zahlen.push({ was: "Offen", wert: eur(wert.offen), ton: "warn" });
  }
  if (wert?.bar_netto) {
    zahlen.push({
      was: "Bar bezahlt", wert: eur(wert.bar_netto), ton: "gut",
      titel: `netto · ${wert.bar_brutto?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} brutto bar erhalten`,
    });
  }
  // Abgeschlossen ohne zugeordnete Rechnung (Altbestand): der alte Wert bleibt, ist aber nicht belegt.
  if (wert?.ohne_rechnung) {
    zahlen.push({ was: "Rechnung", wert: "fehlt", ton: "warn",
      titel: "Abgeschlossen, aber keine Rechnung zugeordnet — der Auftragswert ist der alte, nicht belegte Wert." });
  }
  if (wert && wert.nicht_abgerechnet > 0) zahlen.push({ was: "Noch nicht abgerechnet", wert: eur(wert.nicht_abgerechnet), ton: "akzent" });
  zahlen.push({
    was: "Gewinn",
    wert: `${eur(gewinn)} · ${marge.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`,
    ton: gewinn >= 0 ? "gut" : "schlecht",
    titel: gewinnWeg,
  });
  if (stundenlohn) {
    const h = (n: number) => n.toLocaleString("de-DE", { maximumFractionDigits: 1 });
    zahlen.push({
      was: "Stundenlohn Inhaber",
      // Ohne Inhaberstunden ist der Wert nicht gerechnet, nicht null.
      wert: stundenlohn.inhaberStunden > 0 ? `${eur(stundenlohn.betrag)}/h` : "—",
      titel: `Gewinn je Inhaberstunde · ${h(stundenlohn.inhaberStunden)} h Inhaber, ${h(stundenlohn.helferStunden)} h Helfer`,
    });
  }

  return (
    <div className="pk-zahlen-block">
      <dl className="pk-zahlen">
        {zahlen.map((z) => (
          <div key={z.was} className={`pk-zahl${z.ton ? ` is-${z.ton}` : ""}`} title={z.titel}>
            <dt>{z.was}</dt>
            <dd>{z.wert}</dd>
          </div>
        ))}
      </dl>
      {wert?.hinweis && <p className="pk-meta">{wert.hinweis}</p>}
    </div>
  );
}
